import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Star, Users, Flame } from "lucide-react";
import { AltalabLogo } from "../components/AltalabLogo";

// Background cards component - Tinder style
const BackgroundCards = () => {
  const cards = [
    { name: "Laura", age: 25, rotation: -15, x: "5%", y: "10%", delay: 0 },
    { name: "Pedro", age: 28, rotation: 12, x: "75%", y: "5%", delay: 0.1 },
    { name: "Ana", age: 23, rotation: -8, x: "15%", y: "55%", delay: 0.2 },
    { name: "Lucas", age: 26, rotation: 18, x: "70%", y: "50%", delay: 0.3 },
    { name: "Julia", age: 24, rotation: -20, x: "40%", y: "75%", delay: 0.4 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {cards.map((card, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ delay: card.delay, duration: 0.8 }}
          className="absolute w-48 h-64 rounded-2xl bg-gradient-to-b from-white/10 to-white/5 border border-white/10 backdrop-blur-sm"
          style={{
            left: card.x,
            top: card.y,
            transform: `rotate(${card.rotation}deg)`,
          }}
        >
          <div className="absolute bottom-4 left-4 right-4">
            <div className="h-3 w-20 bg-white/30 rounded mb-2" />
            <div className="h-2 w-12 bg-white/20 rounded" />
          </div>
        </motion.div>
      ))}
    </div>
  );
};

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    displayName: "",
  });

  const testimonials = [
    {
      name: "Marina S.",
      role: "250k seguidores",
      text: "Triplicei minha base de assinantes em 2 meses",
      image: "https://images.pexels.com/photos/16283522/pexels-photo-16283522.jpeg",
    },
    {
      name: "Rafael M.",
      role: "180k seguidores",
      text: "A verificação dá segurança de trabalhar com profissionais",
      image: "https://images.pexels.com/photos/6835960/pexels-photo-6835960.jpeg",
    },
    {
      name: "Julia K.",
      role: "320k seguidores",
      text: "Encontrei parceiros incríveis para collabs",
      image: "https://images.pexels.com/photos/3992656/pexels-photo-3992656.png",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success("Bem-vindo de volta!");
        // Redirect admin users to admin panel
        if (userData.is_admin) {
          navigate("/admin");
        } else if (!userData.onboarding_completed) {
          navigate("/onboarding");
        } else {
          navigate("/discovery");
        }
      } else {
        if (!formData.username || !formData.displayName) {
          toast.error("Preencha todos os campos");
          setLoading(false);
          return;
        }
        await register(formData.email, formData.username, formData.password, formData.displayName);
        toast.success("Conta criada com sucesso!");
        navigate("/onboarding");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Algo deu errado";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex flex-col lg:flex-row">
      {/* Left Side - Branding & Social Proof (Hidden on mobile, shown as top section) */}
      <div className="relative lg:w-1/2 lg:min-h-screen overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FD5564]/10 via-transparent to-[#FF7854]/5" />
        
        {/* Tinder-style background cards */}
        <BackgroundCards />
        
        <div className="relative z-10 flex flex-col justify-between p-8 lg:p-12 min-h-[50vh] lg:min-h-screen">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <AltalabLogo size="large" />
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 flex flex-col justify-center py-8 lg:py-0"
          >
            <h1 className="font-heading text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tighter leading-tight mb-6">
              Onde creators
              <br />
              <span className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] bg-clip-text text-transparent">
                se conectam
              </span>
            </h1>

            <p className="text-white/50 text-base lg:text-lg max-w-md leading-relaxed mb-8 lg:mb-12">
              A plataforma exclusiva para criadores de conteúdo que querem crescer através de colaborações estratégicas.
            </p>

            {/* Stats */}
            <div className="flex gap-6 lg:gap-8 mb-8 lg:mb-12">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FD5564]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#FD5564]" />
                </div>
                <div>
                  <div className="font-bold text-lg lg:text-xl">500+</div>
                  <div className="text-white/40 text-xs lg:text-sm">Creators verificados</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="font-bold text-lg lg:text-xl">12.8k</div>
                  <div className="text-white/40 text-xs lg:text-sm">Collabs realizadas</div>
                </div>
              </div>
            </div>

            {/* Testimonials Carousel - Hidden on mobile */}
            <div className="relative h-24 hidden lg:block">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonials[currentTestimonial].image}
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-[#FD5564]/30"
                    />
                    <div>
                      <p className="text-white/70 italic text-sm mb-1">
                        "{testimonials[currentTestimonial].text}"
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{testimonials[currentTestimonial].name}</span>
                        <span className="text-white/30">•</span>
                        <span className="text-white/40 text-sm">{testimonials[currentTestimonial].role}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Testimonial indicators */}
              <div className="absolute bottom-0 left-16 flex gap-2">
                {testimonials.map((_, index) => (
                  <div
                    key={index}
                    className={`w-8 h-1 rounded-full transition-colors ${
                      index === currentTestimonial ? "bg-[#FD5564]" : "bg-white/20"
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Bottom badge - Hidden on mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="hidden lg:flex items-center gap-2 text-white/30 text-sm"
          >
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Comunidade ativa 24/7</span>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-[#0a0a0a] lg:bg-transparent">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo - Hidden on desktop */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <AltalabLogo />
          </div>

          {/* Form Header */}
          <div className="mb-8">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold mb-2">
              {isLogin ? "Entre na comunidade" : "Crie sua conta"}
            </h2>
            <p className="text-white/50 text-sm lg:text-base">
              {isLogin
                ? "Conecte-se com creators incríveis"
                : "Comece sua jornada no altalab"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  key="register-fields"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 lg:space-y-5"
                >
                  <div className="space-y-2">
                    <Label htmlFor="displayName" className="text-white/70 text-sm">
                      Nome de Exibição
                    </Label>
                    <Input
                      id="displayName"
                      name="displayName"
                      placeholder="Como você quer ser chamado(a)"
                      value={formData.displayName}
                      onChange={handleChange}
                      className="h-12 bg-white/5 border-white/10 focus:border-[#FD5564] rounded-xl text-sm lg:text-base"
                      data-testid="display-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-white/70 text-sm">
                      Username
                    </Label>
                    <Input
                      id="username"
                      name="username"
                      placeholder="@seunome"
                      value={formData.username}
                      onChange={handleChange}
                      className="h-12 bg-white/5 border-white/10 focus:border-[#FD5564] rounded-xl text-sm lg:text-base"
                      data-testid="username-input"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70 text-sm">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 bg-white/5 border-white/10 focus:border-[#FD5564] rounded-xl text-sm lg:text-base"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70 text-sm">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white/5 border-white/10 focus:border-[#FD5564] rounded-xl pr-12 text-sm lg:text-base"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 lg:h-14 bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:from-[#FD5564]/90 hover:to-[#FF7854]/90 rounded-full font-semibold text-base lg:text-lg shadow-lg shadow-[#FD5564]/25"
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isLogin ? (
                "Entrar"
              ) : (
                "Criar Conta"
              )}
            </Button>
          </form>

          {/* Toggle */}
          <div className="mt-6 lg:mt-8 text-center">
            <p className="text-white/50 text-sm lg:text-base">
              {isLogin ? "Não tem uma conta?" : "Já tem uma conta?"}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-[#FD5564] hover:text-[#FF7854] font-medium"
                data-testid="toggle-auth-btn"
              >
                {isLogin ? "Criar conta" : "Fazer login"}
              </button>
            </p>
          </div>

          {/* Mobile Stats - Shown only on mobile */}
          <div className="lg:hidden mt-8 pt-6 border-t border-white/10">
            <div className="flex justify-center gap-8">
              <div className="text-center">
                <div className="font-bold text-lg">500+</div>
                <div className="text-white/40 text-xs">Creators</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg">12.8k</div>
                <div className="text-white/40 text-xs">Collabs</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  4.9
                </div>
                <div className="text-white/40 text-xs">Rating</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
